import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RecommendationFilterState } from '@/types/recommendation';

export const DEFAULT_RECOMMENDATION_FILTER: RecommendationFilterState = {
  searchQuery: '',
  roleFilter: null,
  departmentFilter: null,
};

interface RecommendationFilterPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filter: RecommendationFilterState;
  onFilterChange: (filter: RecommendationFilterState) => void;
  availableRoles: string[];
  availableLocations: string[];
  availableDepartments: string[];
  trigger: React.ReactNode;
}

export function RecommendationFilterPopover({
  open,
  onOpenChange,
  filter,
  onFilterChange,
  availableRoles,
  availableDepartments,
  trigger,
}: RecommendationFilterPopoverProps) {
  const handleReset = () => {
    onFilterChange({
      ...DEFAULT_RECOMMENDATION_FILTER,
      searchQuery: filter.searchQuery,
    });
  };

  const handleApply = () => {
    onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-76 p-0" align="end">
        {/* Header */}
        <div className="px-4 py-3 border-b">
          <p className="text-sm font-semibold text-foreground">Filters</p>
          <p className="text-xs text-muted-foreground mt-0.5">Narrow down resource suggestions</p>
        </div>

        <div className="px-4 py-3 space-y-4 max-h-[420px] overflow-y-auto">
          {/* Resource attributes */}
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Resource attributes
            </p>

            {/* Role */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Role</Label>
              <Select
                value={filter.roleFilter || '__any__'}
                onValueChange={v =>
                  onFilterChange({ ...filter, roleFilter: v === '__any__' ? null : v })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Any role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any role</SelectItem>
                  {availableRoles.map(r => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Department */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Department</Label>
              <Select
                value={filter.departmentFilter || '__any__'}
                onValueChange={v =>
                  onFilterChange({ ...filter, departmentFilter: v === '__any__' ? null : v })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Any department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any department</SelectItem>
                  {availableDepartments.map(d => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
          <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={handleReset}>
            Reset all
          </Button>
          <Button type="button" variant="default" size="sm" className="h-8 text-xs" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
